import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
  Grid,
  Link,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import {FileCopyOutlined, AssignmentOutlined} from '@material-ui/icons';
import {APICall} from '../api/api';
import {IABcategoriesTable} from '../components/subcomponents/IABcategoriesTable';
import {DomainSelect} from '../components/subcomponents/Blocklist/DomainSelect';
import {CustomUrlList} from '../components/subcomponents/Blocklist/CustomUrlList';
import {
  urlsFromSelect,
  urlsToOptions,
  createSnapshot,
  sameItems,
  buildTicketSummary,
  serializeBlocklistClipboard,
  parseBlocklistClipboard,
} from '../components/subcomponents/Blocklist/blocklistHelpers';
import useStore from '../store/store';
import useSnackbarSuccessOrError from '../components/subcomponents/subscription/customerSuccessBar';
import usePersistentAuthStore from '../store/persistentAuthStore';
import {Alert} from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
  viewWrapper: {
    padding: 50,
    [theme.breakpoints.down('sm')]: {padding: 15, paddingTop: 10},
  },
  blocklistSelectIconBtn: {
    padding: 5,
    '& .MuiSvgIcon-root': {
      fontSize: 19,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      opacity: 1,
    },
  },
  blocklistSelectPasteIconBtn: {
    color: theme.palette.text.secondary,
    '&:hover:not(.Mui-disabled)': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  },
  blocklistCopyOutlinedBtn: {
    flexShrink: 0,
    textTransform: 'none',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.15,
    padding: '5px 12px',
    minWidth: 0,
    lineHeight: 1.2,
    marginRight: theme.spacing(0.5),
    boxShadow: 'none',
    '& .MuiButton-startIcon': {
      marginRight: 6,
      marginLeft: -2,
    },
    '& .MuiSvgIcon-root': {
      fontSize: 17,
    },
  },
}));

const BlockList = ({getTickets}) => {
  const classes = useStyles();
  const {auth} = usePersistentAuthStore();
  const [snackbar] = useSnackbarSuccessOrError();
  const setState = useStore(s => s.setState);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState(null);
  const [blockedUrls, setBlockedUrls] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blocklistClipboard, setBlocklistClipboard] = useState(null);

  const domainId = selectedDomains?.value ?? selectedDomains?.[0]?.value ?? null;
  const currentUrls = useMemo(() => urlsFromSelect(blockedUrls), [blockedUrls]);

  const hasChanges =
    snapshot &&
    (!sameItems(selectedCategories, snapshot.categoryIds) ||
      !sameItems(currentUrls, snapshot.urls));

  const isWrapperClient = auth.type === 1;

  const onLoaded = useCallback(({categories, urls}) => {
    setBlockedUrls(urlsToOptions(urls));
    setSnapshot(createSnapshot(categories, urls));
  }, []);

  useEffect(() => {
    if (!domainId) setBlockedUrls([]);
    setSnapshot(null);
  }, [domainId]);

  const copyBlocklist = useCallback(() => {
    if (!domainId || !snapshot) return;
    if (!selectedCategories.length && !currentUrls.length) return;
    const payload = {
      categoryIds: selectedCategories.map(String),
      urls: [...currentUrls],
    };
    setBlocklistClipboard(payload);
    const json = serializeBlocklistClipboard(payload.categoryIds, payload.urls);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(json).catch(() => {});
    }
    snackbar(true, 'Blocklist copied. Select another domain, then paste.', '');
  }, [domainId, snapshot, selectedCategories, currentUrls, snackbar]);

  const pasteBlocklist = useCallback(async () => {
    if (!domainId || !snapshot) return;
    let payload = blocklistClipboard;
    if (!payload && navigator.clipboard?.readText) {
      try {
        const text = await navigator.clipboard.readText();
        payload = parseBlocklistClipboard(text);
      } catch {
        payload = null;
      }
    }
    if (!payload) {
      snackbar(false, '', 'Nothing to paste. Use Copy blocklist on a domain first.');
      return;
    }
    setSelectedCategories([...new Set(payload.categoryIds.map(String))]);
    setBlockedUrls(urlsToOptions(payload.urls));
    snackbar(true, 'Blocklist pasted. Review changes, then save.', '');
  }, [domainId, snapshot, blocklistClipboard, snackbar]);

  const blocklistDomainAdornment = useMemo(() => {
    const canCopy =
      Boolean(domainId && snapshot) && (selectedCategories.length > 0 || currentUrls.length > 0);
    return (
      <>
        <Tooltip
          title={
            canCopy
              ? 'Copy categories and advertiser URLs to use on another domain'
              : 'Add at least one blocked category or advertiser URL first'
          }
        >
          <span>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={!canCopy}
              onClick={copyBlocklist}
              onMouseDown={e => e.stopPropagation()}
              startIcon={<FileCopyOutlined />}
              className={classes.blocklistCopyOutlinedBtn}
            >
              Copy blocklist settings
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Paste blocklist settings">
          <span>
            <IconButton
              className={`${classes.blocklistSelectIconBtn} ${classes.blocklistSelectPasteIconBtn}`}
              size="small"
              disabled={!domainId || !snapshot}
              onClick={pasteBlocklist}
              aria-label="Paste blocklist"
            >
              <AssignmentOutlined />
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  }, [
    classes.blocklistCopyOutlinedBtn,
    classes.blocklistSelectIconBtn,
    classes.blocklistSelectPasteIconBtn,
    copyBlocklist,
    currentUrls.length,
    domainId,
    pasteBlocklist,
    selectedCategories.length,
    snapshot,
  ]);

  const authHeaders = {
    Authorization: `Bearer ${auth.token}`,
    'Cache-Control': 'max-age=0, no-cache',
    'Content-Type': 'application/json',
  };

  const save = async () => {
    if (!domainId) return;
    setSaving(true);
    try {
      const res = await APICall({
        endpoint: 'updateBlocklist',
        options: {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            domainId: [domainId],
            iabCategoryIds: selectedCategories,
            blocklistUrls: currentUrls,
          }),
        },
      });
      if (!res?.success) return snackbar(false, '', res?.error || 'Internal server error');

      if (isWrapperClient) {
        const ticket = await APICall({
          endpoint: 'submitTicket',
          options: {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              title: 'Blocklist request',
              summary: buildTicketSummary({
                snapshot,
                selectedCategories,
                currentUrls,
                selectedDomains,
              }),
              issueType: 'Blocklist',
            }),
          },
        });
        if (!ticket?.success)
          return snackbar(false, '', ticket?.error || 'Ticket submission failed');
        getTickets?.();
      } else {
        setState('completedStep', 4);
      }

      setSnapshot(prev => ({
        ...prev,
        categoryIds: [...selectedCategories].map(String).sort(),
        urls: [...currentUrls].sort(),
      }));
      snackbar(
        true,
        isWrapperClient
          ? 'Blocklist change requested - check support section for status updates.'
          : 'Blocklist updated successfully.',
        ''
      );
    } catch {
      snackbar(false, '', 'Internal server error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid
      container
      component="main"
      maxWidth="xl"
      className={classes.viewWrapper}
      justifyContent="center"
      alignItems="center"
    >
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom>
          Prebid blocklist management
        </Typography>
      </Grid>

      {isWrapperClient && (
        <Grid item xs={12}>
          <Typography gutterBottom>
            Select a domain and choose IAB advertiser categories to block. Refer to the{' '}
            <Link href="/support">Support</Link> section to follow up on blocklist updates requests.
          </Typography>
        </Grid>
      )}

      {!isWrapperClient && (
        <Grid container>
          <Grid item xs={12}>
            <Alert severity="warning" variant="filled" style={{width: 'fit-content'}}>
              Prebid.js v10.22.0 or higher is required for this feature to work.
            </Alert>
          </Grid>
        </Grid>
      )}

      <Grid
        container
        spacing={4}
        justifyContent="center"
        alignItems="center"
        style={{marginTop: 5, marginBottom: 5}}
      >
        <Grid item xs={12}>
          <Typography gutterBottom color="textSecondary" variant="subtitle" display="block">
            1. Select domain:
          </Typography>
          <DomainSelect
            selectedDomains={selectedDomains}
            setSelectedDomains={setSelectedDomains}
            insideControlAdornment={blocklistDomainAdornment}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography gutterBottom color="textSecondary" variant="subtitle" display="block">
            2. Add specific advertisers to the blocklist:
          </Typography>
          <CustomUrlList
            listOfUrls={blockedUrls ?? []}
            setListofUrls={v => setBlockedUrls(v ?? [])}
            isDisabled={!domainId}
            canUseClipboard={Boolean(domainId && snapshot)}
            onNotify={snackbar}
          />
        </Grid>
      </Grid>

      <IABcategoriesTable
        domainId={domainId}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        onBlocklistLoaded={onLoaded}
      />

      <Grid item xs={12} style={{marginTop: 30, marginBottom: 30}}>
        <Button
          variant="contained"
          color="primary"
          disabled={saving || !domainId || !hasChanges}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
          onClick={save}
        >
          {saving
            ? 'Please wait'
            : isWrapperClient
              ? 'Request blocklist update'
              : 'Update blocklist'}
        </Button>
      </Grid>
    </Grid>
  );
};

export default React.memo(BlockList);
